import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";
import { Check, Copy, Loader2, Send, Sparkles } from "lucide-react";
import moment from "moment";

import { MarkdownRenderer } from "@/modern/components/ui/markdown-renderer";
import { cn } from "@/modern/utils";
import { sendChatMessage } from "@/network/network";

import type {
  ChatMessage,
  SendMessageRequest,
  SendMessageResponse,
} from "@/modern/components/chatbox/types";

import { SURFACE_CONTROL_CLS } from "../constants/layout";
import { useNotificationScope } from "../hooks/useNotificationScope";
import type { MappedNotificationCounts } from "../types";
import {
  askScopeLabel,
  buildAskEmptySuggestions,
  buildUpdatesAskUiContext,
  type UpdatesAskOrigin,
} from "../utils/askUiContext";

const COMPOSER_MAX_HEIGHT_PX = 120;

const COMPOSER_CLS = cn(
  "flex max-h-[120px] min-h-11 w-full resize-none overflow-y-hidden rounded-full py-3 pl-4 pr-12 text-base outline-none",
  SURFACE_CONTROL_CLS,
  "placeholder:text-base placeholder:text-muted-foreground",
  "focus-visible:border-ring focus-visible:text-foreground focus-visible:ring-1 focus-visible:ring-ring",
);

function AssistantCopyButton({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable; keep quiet.
    }
  };

  return (
    <button
      type="button"
      onClick={() => {
        void onCopy();
      }}
      className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={copied ? "Copied" : "Copy response"}
    >
      {copied ? (
        <Check className="size-3.5" aria-hidden />
      ) : (
        <Copy className="size-3.5" aria-hidden />
      )}
    </button>
  );
}

export function NotificationAskView({
  origin,
  notableTotal,
  notableUnread,
  routineTotal,
  routineUnread,
  routineCategories,
  messages,
  sessionId,
  isLoading,
  onMessagesChange,
  onSessionIdChange,
  onLoadingChange,
}: {
  origin: UpdatesAskOrigin;
  notableTotal: number;
  notableUnread: number;
  routineTotal: number;
  routineUnread: number;
  routineCategories: MappedNotificationCounts["categories"];
  /** Lifted to the Updates popup so Ask keeps one session while the modal is open. */
  messages: ChatMessage[];
  sessionId: string | undefined;
  isLoading: boolean;
  onMessagesChange: Dispatch<SetStateAction<ChatMessage[]>>;
  onSessionIdChange: (sessionId: string | undefined) => void;
  onLoadingChange: (loading: boolean) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { boardId, isAccountView } = useNotificationScope();

  const [input, setInput] = useState("");

  const emptySuggestions = useMemo(() => {
    const built = buildAskEmptySuggestions(origin, {
      notableUnread,
      routineUnread,
      routineCategories,
    });
    const seen = new Set<string>();
    return built.filter((tile) => {
      const key = tile.prompt.trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [notableUnread, origin, routineCategories, routineUnread]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    // scrollHeight excludes borders; without them the content overflows by ~2px and a scrollbar shows.
    const { borderTopWidth, borderBottomWidth } = window.getComputedStyle(el);
    const fullHeight =
      el.scrollHeight + parseFloat(borderTopWidth) + parseFloat(borderBottomWidth);
    el.style.height = `${Math.min(fullHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
    el.style.overflowY = fullHeight > COMPOSER_MAX_HEIGHT_PX ? "auto" : "hidden";
  }, [input]);

  const buildAskPayload = useCallback(
    (query: string, currentSessionId?: string): SendMessageRequest => {
      const payload: SendMessageRequest = {
        query,
        local_datetime: moment().format("YYYY-MM-DDTHH:mm:ssZ"),
        ui_context: buildUpdatesAskUiContext({
          origin,
          notableTotal,
          notableUnread,
          routineTotal,
          routineUnread,
          routineCategories,
        }),
      };
      if (currentSessionId) {
        payload.session_id = currentSessionId;
      }
      if (!isAccountView && boardId != null) {
        payload.board_id = boardId;
      }
      return payload;
    },
    [
      boardId,
      isAccountView,
      notableTotal,
      notableUnread,
      origin,
      routineCategories,
      routineTotal,
      routineUnread,
    ],
  );

  const handleSend = async (text?: string) => {
    const message = (text ?? input).trim();
    if (!message || isLoading) return;

    setInput("");
    const userMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: message,
      created_at: new Date().toISOString(),
    };
    onMessagesChange((prev) => [...prev, userMsg]);
    onLoadingChange(true);

    try {
      const payload = buildAskPayload(message, sessionId);
      const response = await sendChatMessage(payload);
      const data: SendMessageResponse = response.data;

      if (data.session_id) {
        onSessionIdChange(data.session_id);
      }

      const assistantMsg: ChatMessage = {
        id: data.message_id || `resp-${Date.now()}`,
        role: "assistant",
        content: data.answer,
        created_at: new Date().toISOString(),
      };
      onMessagesChange((prev) => [...prev, assistantMsg]);
    } catch {
      const errorMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        role: "assistant",
        content: "Sorry, I couldn't process that request. Please try again.",
        created_at: new Date().toISOString(),
      };
      onMessagesChange((prev) => [...prev, errorMsg]);
    } finally {
      onLoadingChange(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const hasMessages = messages.length > 0;
  const scopeLabel = askScopeLabel(origin);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-background">
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto px-5 pt-3 pb-3 sm:px-7"
      >
        {hasMessages ? (
          <div className="flex w-full flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Sparkles className="size-3.5" aria-hidden />
                Continuing · {scopeLabel.replace(/^Scope ·\s*/, "")}
              </span>
            </div>

            {messages.map((msg) =>
              msg.role === "user" ? (
                <div key={msg.id} className="flex justify-end">
                  <div className="max-w-[min(100%,36rem)] rounded-[1.25rem] bg-sky-500/10 px-4 py-2.5 text-base leading-relaxed text-sky-900 dark:bg-sky-400/15 dark:text-sky-100">
                    <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                  </div>
                </div>
              ) : (
                <div key={msg.id} className="flex flex-col gap-2">
                  <MarkdownRenderer
                    content={msg.content}
                    className={cn(
                      "max-w-none text-base text-foreground",
                      "prose-base prose-p:my-0 prose-p:text-base prose-p:leading-relaxed",
                      "[&_p+p]:mt-3",
                      "prose-headings:mb-2 prose-headings:mt-0 prose-headings:text-base prose-headings:font-semibold",
                      "prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-li:text-base",
                      "prose-strong:font-semibold",
                    )}
                  />
                  <div className="flex items-center gap-0.5">
                    <AssistantCopyButton content={msg.content} />
                  </div>
                </div>
              ),
            )}

            {isLoading ? (
              <div className="flex items-center gap-1.5 py-1" aria-live="polite">
                <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:0ms]" />
                <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:150ms]" />
                <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:300ms]" />
                <span className="sr-only">Swivu is responding</span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex h-full min-h-0 flex-col items-center justify-center px-2 text-center">
            <div
              className={cn(
                "mb-5 flex size-14 items-center justify-center rounded-2xl",
                SURFACE_CONTROL_CLS,
              )}
              aria-hidden
            >
              <Sparkles className="size-6 text-foreground/70" />
            </div>
            <h3 className="text-2xl font-normal tracking-tight text-foreground sm:text-3xl">
              Ask about these updates
            </h3>
            <p className="mt-2 max-w-md text-base leading-snug text-muted-foreground sm:text-lg">
              Try a suggestion below, or type your own question.
            </p>
            <div
              className="mt-8 flex max-w-3xl flex-wrap items-center justify-center gap-2"
              aria-label="Suggested questions"
            >
              {emptySuggestions.map((tile) => (
                <button
                  key={tile.prompt}
                  type="button"
                  disabled={isLoading}
                  onClick={() => void handleSend(tile.prompt)}
                  className={cn(
                    "inline-flex h-11 items-center rounded-full px-4 text-base font-medium",
                    SURFACE_CONTROL_CLS,
                    "transition-[transform,background-color,color,border-color] duration-150 ease-out",
                    "hover:border-input-enhanced hover:text-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    "active:scale-[0.98] motion-reduce:active:scale-100",
                    "disabled:pointer-events-none disabled:opacity-40",
                  )}
                >
                  {tile.prompt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 px-5 py-3 sm:px-7">
        <div className="relative">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about these updates…"
            rows={1}
            disabled={isLoading}
            className={COMPOSER_CLS}
          />
          <button
            type="button"
            disabled={!input.trim() || isLoading}
            onClick={() => void handleSend()}
            className={cn(
              "absolute right-1.5 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full transition-[transform,background-color,opacity] duration-150",
              "bg-primary text-primary-foreground hover:bg-primary/90",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "active:scale-[0.97] motion-reduce:active:scale-100",
              "disabled:pointer-events-none disabled:opacity-40",
            )}
            aria-label="Send"
          >
            {isLoading ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <Send className="size-3.5" aria-hidden />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
